import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  addDriveSystem,
  removeDriveSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { DriveSystem } from "../../../../types/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";

import {
  selectHighBurnThrusterStats,
  createConcealedManeuverDriveStatsSelector,
  createSolarSailStatsSelector,
} from '../../../../store/selectors/derivedSelectors';

interface DriveSystemsProps {
  availableSystems: DriveSystem[];
  shipTonnage: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

const DriveSystems: React.FC<DriveSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.drives);
  const existingDrives = useAppSelector((state) => state.shipBuilder.drives);
  const baseManeuverDrive = existingDrives.find((d) => d.type === "maneuver");

  // State for High-Burn Thruster thrust selection
  const [thrusterThrust, setThrusterThrust] = useState<number>(1);

  // Precompute selector results for each system
  const highBurnStats = useAppSelector((state) =>
    Object.fromEntries(
      availableSystems
        .filter((system) => system.subtype === "highBurn")
        .map((system) => [
          system.id,
          selectHighBurnThrusterStats(state, thrusterThrust),
        ]),
    ),
  );
  const concealedStats = useAppSelector((state) =>
    Object.fromEntries(
      availableSystems
        .filter((system) => system.subtype === "concealed")
        .map((system) => {
          const selector = createConcealedManeuverDriveStatsSelector(
            shipTonnage,
            existingDrives,
          );
          return [system.id, selector(state)];
        }),
    ),
  );
  const solarSailStats = useAppSelector((state) =>
    Object.fromEntries(
      availableSystems
        .filter((system) => system.subtype === "solarSail")
        .map((system) => {
          const selector = createSolarSailStatsSelector(
            shipTonnage,
            (system.properties?.costPerTon as number) || 0,
            (system.properties?.tonnagePercent as number) || 0,
          );
          return [system.id, selector(state)];
        }),
    ),
  );

  // Prepare available systems with dynamic calculations and validation
  const processedAvailableSystems = availableSystems.map((system) => {
    let calculatedCost = system.cost;
    let calculatedTonnage = system.tonnage;
    let calculatedPower = system.power;
    let validationErrors: string[] = [];
    let isInstalled = systems.some((s) => s.id === system.id);

    // Special handling for High-Burn Thruster
    if (system.subtype === "highBurn") {
      const stats = highBurnStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
    } else if (system.subtype === "concealed") {
      const stats = concealedStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
      // Validation for Concealed Maneuver Drive
      if (!baseManeuverDrive) {
        validationErrors.push("Requires existing maneuver drive");
      }
    } else if (system.subtype === "solarSail") {
      const stats = solarSailStats[system.id];
      calculatedCost = stats.cost;
      calculatedTonnage = stats.tonnage;
      calculatedPower = stats.power;
    }

    // Tech level validation
    if (!tlOverrideActive && (system.techLevel ?? 0) > techLevel) {
      validationErrors.push(`Requires TL ${system.techLevel}`);
    }

    return {
      ...system,
      cost: calculatedCost,
      tonnage: calculatedTonnage,
      power: calculatedPower,
      validationErrors,
      isInstalled,
    };
  });

  const handleAddSystem = (system: DriveSystem) => {
    let finalSystem = { ...system };

    // Special handling for High-Burn Thruster
    if (system.subtype === "highBurn") {
      const stats = highBurnStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
        properties: {
          ...system.properties,
          selectedThrust: thrusterThrust,
        },
      };
    } else if (system.subtype === "concealed") {
      const stats = concealedStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
        properties: {
          ...system.properties,
          calculated: true,
          baseManeuverDrive: baseManeuverDrive,
        },
      };
    } else if (system.subtype === "solarSail") {
      const stats = solarSailStats[system.id];
      finalSystem = {
        ...system,
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
        properties: {
          ...system.properties,
        },
      };
    }

    dispatch(addDriveSystem(finalSystem));
  };

  const handleRemoveSystem = (systemId: string) => {
    dispatch(removeDriveSystem(systemId));
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Drive Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Available Drive Systems</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {processedAvailableSystems.map((system) => (
            <div
              key={system.id}
              className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
            >
              <div>
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {system.name}
                </div>

                {/* Special UI for High-Burn Thruster */}
                {system.subtype === "highBurn" && (
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Thrust Rating
                    </label>
                    <select
                      value={thrusterThrust}
                      onChange={(e) =>
                        setThrusterThrust(Number(e.target.value))
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                      disabled={system.isInstalled}
                    >
                      {[1, 2, 3, 4, 5, 6].map((val) => (
                        <option key={val} value={val}>
                          Thrust {val}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <ul className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                  <li>
                    <b>Cost:</b> {formatCurrency(system.cost)} Cr
                  </li>
                  <li>
                    <b>Tonnage:</b> {system.tonnage.toFixed(1)} tons
                  </li>
                  <li>
                    <b>Power:</b> {system.power}
                  </li>

                  {/* Special properties for each system type */}
                  {system.subtype === "concealed" && (
                    <>
                      <li>
                        <b>Cost/Tonnage:</b> +25% of M-drive
                      </li>
                    </>
                  )}
                </ul>

                {system.validationErrors.length > 0 && (
                  <div className="text-xs text-red-600 dark:text-red-400 mb-2">
                    {system.validationErrors.map((err, idx) => (
                      <div key={idx}>{err}</div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-2 flex gap-2">
                {!system.isInstalled ? (
                  <button
                    className={`w-full mt-2 px-3 py-1 rounded text-white font-semibold ${
                      system.validationErrors.length > 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                    onClick={() => handleAddSystem(system as DriveSystem)}
                    disabled={system.validationErrors.length > 0}
                    title={
                      system.validationErrors.length > 0
                        ? system.validationErrors.join(" ")
                        : ""
                    }
                  >
                    Add
                  </button>
                ) : (
                  <button
                    className="px-4 py-1 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                    onClick={() => handleRemoveSystem(system.id)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriveSystems;
